import { JetView } from 'webix-jet'; // Importing JetView from webix-jet for creating views
import { devConfig, countries, profile } from 'config/config'; // Importing configuration settings

/**
 * Class representing the FilterBonusReportView.
 * @extends JetView
 */
export default class FilterBonusReportView extends JetView {
    /**
     * Configures the view layout and components.
     * @returns {Object} The configuration object for the view.
     */
    config() {
        const _ = this.app.getService('locale')._; // Localization service
        const screen = this.app.config.size; // Get the current screen size

        // Left section configuration
        const left = {
            margin: 5,
            rows: [
                {
                    margin: 15,
                    css: 'on-row', // CSS class for styling
                    cols: [
                        {
                            view: 'text', // Text input for date range
                            name: 'Datepicker',
                            id: 'Datepicker',
                            css: 'reportrange', // CSS class for styling
                            value:
                                moment().subtract(0, 'days').format('YYYY-MM-DD') +
                                ' - ' +
                                moment().format('YYYY-MM-DD'), // Default value for date range
                            label: "<span class='webix_icon icon-calendar'></span>" + _('Fecha'), // Label for the input
                            labelPosition: 'top',
                            placeholder: _('Click para seleccionar '), // Placeholder text
                        },
                        {
                            view: 'datepicker', // Datepicker component
                            name: 'Date',
                            id: 'Date',
                            stringResult: true, // Return string format
                            minWidth: 200,
                            label: "<span class='webix_icon icon-calendar'></span>" + _('Fecha'), // Label for the datepicker
                            labelPosition: 'top',
                            placeholder: _('Click para seleccionar '), // Placeholder text
                            value: moment().format('YYYY-MM-DD'), // Default date value
                            format: '%d %M %Y', // Date format
                            suggest: {
                                type: 'calendar', // Suggestion type
                                body: {
                                    maxDate: new Date(), // Maximum selectable date
                                },
                            },
                        },
                        {
                            view: 'richselect', // Dropdown for country selection
                            name: 'CountrySelect',
                            id: 'CountrySelect',
                            localId: 'regions:combo',
                            label: "<span class='icon-language'></span>" + _('País'), // Label for the dropdown
                            labelPosition: 'top',
                            placeholder: _('Click para seleccionar '), // Placeholder text
                            options: {
                                body: {
                                    template: function (obj) {
                                        return (
                                            "<i class='flags " + obj.icon + "'></i>" + obj.value // Template for displaying country flags
                                        );
                                    },
                                    data: countries, // Data source for countries
                                },
                            },
                            hidden:
                                devConfig.getCountryReport() || // Conditional visibility based on config
                                !devConfig.getPermAction('filterByCountry', 'Add'),
                            css: 'prm-filterByCountry', // CSS class for styling
                        },
                        {
                            view: 'richselect', // Second dropdown for country selection
                            name: 'CountrySelect2',
                            localId: 'regions:combo',
                            label: "<span class='icon-language'></span>" + _('País'), // Label for the dropdown
                            labelPosition: 'top',
                            placeholder: _('Click para seleccionar '), // Placeholder text
                            value: profile.Settings.ReportCountry, // Default value from profile settings
                            readonly: true, // Make the dropdown read-only
                            options: {
                                body: {
                                    template: function (obj) {
                                        return (
                                            "<i class='flags " + obj.icon + "'></i>" + obj.value // Template for displaying country flags
                                        );
                                    },
                                    data: countries, // Data source for countries
                                },
                            },
                            hidden:
                                !devConfig.getCountryReport() || // Conditional visibility based on config
                                !devConfig.getPermAction('filterByCountry', 'Add'),
                            css: 'prm-filterByCountry country-hide', // CSS classes for styling
                        },
                        {
                            view: 'text', // Text input for Player ID
                            name: 'PlayerId',
                            type: 'number', // Input type
                            label: "<span class='icon-drivers-license-o'></span>" + _('ID Cliente'), // Label for the input
                            labelPosition: 'top',
                            placeholder: _('ID Cliente'), // Placeholder text
                            invalidMessage: _('Client ID is required'), // Error message for validation
                        },
                    ],
                },
            ],
        };

        // Left section 2 configuration
        const left2 = {
            margin: 5,
            rows: [
                {
                    margin: 15,
                    css: 'on-row', // CSS class for styling
                    cols: [
                        {
                            view: 'text', // Text input for Partner Bonus ID
                            name: 'PartnerBonusId',
                            type: 'number', // Input type
                            label: "<span class='icon-id'></span>" + _('Partner Bono Id'), // Label for the input
                            labelPosition: 'top',
                            placeholder: _('Bono ID'), // Placeholder text
                            invalidMessage: _('Partner Bonus Id is required'), // Error message for validation
                        },
                        {
                            view: 'richselect', // Dropdown for Bonus Type selection
                            name: 'BonusType',
                            localId: 'bonusTypes:combo',
                            label: _('Tipo'), // Label for the dropdown
                            labelPosition: 'top',
                            placeholder: _('Click para seleccionar '), // Placeholder text
                            options: [
                                { id: '', value: _('Todos') }, // Option for all types
                                { id: '2', value: _('Deposito') }, // Deposit option
                                { id: '3', value: _('No Deposito') }, // Non-deposit option
                                { id: '6', value: _('FreeBet') }, // FreeBet option
                            ],
                        },
                        {
                            view: 'richselect', // Dropdown for State selection
                            name: 'State',
                            localId: 'bonusStates:combo',
                            label: "<span class='icon-state'></span>" + _('Estado'), // Label for the dropdown
                            labelPosition: 'top',
                            placeholder: _('Click para seleccionar '), // Placeholder text
                            options: [
                                {
                                    id: '',
                                    value: _('Todos'), // Option for all states
                                },
                                {
                                    id: 'A',
                                    value: _('Activo'), // Active state
                                },
                                {
                                    id: 'I',
                                    value: _('Inactivo'), // Inactive state
                                },
                                {
                                    id: 'P',
                                    value: _('Pendiente'), // Pending state
                                },
                                {
                                    id: 'R',
                                    value: _('Redimido'), // Redeemed state
                                },
                                {
                                    id: 'E',
                                    value: _('Expirado'), // Expired state
                                },
                                {
                                    id: 'L',
                                    value: _('Libre'), // Free state
                                },
                            ],
                        },
                    ],
                },
            ],
        };

        // Upper section configuration
        const upper_section = {
            css: 'father-container', // CSS class for styling
            height: document.body.offsetWidth < 500 ? 180 : 0, // Conditional height based on screen width
            rows: [
                {
                    margin: 10,
                    cols: [left], // Include left section
                },
            ],
        };

        // Upper section 2 configuration
        const upper_section2 = {
            css: 'father-container', // CSS class for styling
            height: document.body.offsetWidth < 500 ? 200 : 0, // Conditional height based on screen width
            rows: [
                {
                    margin: 10,
                    cols: [left2], // Include left section 2
                },
            ],
        };

        // Accordion section configuration
        const accordion_section = {
            id: 'b5',
            rows: [
                {
                    multi: true,
                    view: 'accordion', // Accordion component
                    css: 'betShop', // CSS class for styling
                    collapsed: true, // Initial state of the accordion
                    paddingY: 10,
                    type: 'clean',
                    rows: [
                        {
                            header: _('Filtros Avanzados'), // Header for advanced filters
                            body: {
                                padding: 10,
                                rows: [upper_section2], // Include upper section 2
                            },
                        },
                    ],
                },
            ],
        };

        // Buttons section configuration
        const buttons = {
            id: 'b2',
            rows: [
                {
                    margin: 10,
                    responsive: 'b2',
                    cols: [
                        { gravity: 1, id: 'grav' }, // Gravity for layout
                        {
                            view: 'button', // Button to add bonus to balance
                            value: _('Añadir bono a saldo'), // Button label
                            type: 'form',
                            width: screen == 'small' ? 0 : 200, // Conditional width based on screen size
                            minWidth: 200,
                            hidden: !devConfig.getPermAction(
                                'bonusBalanceAdd',
                                'Add', // Conditional visibility based on permissions
                            ),
                            css: 'prm-bonusBalanceAdd', // CSS class for styling
                            click: () => {
                                this.app.callEvent('addBonusBalance:button', [{}]); // Event call to add bonus
                            },
                        },
                        {
                            view: 'button', // Button to clear the form
                            value: _('Limpiar'), // Button label
                            width: screen == 'small' ? 0 : 100, // Conditional width based on screen size
                            minWidth: 200,
                            click: () => {
                                $$('myForm').setValues(
                                    $$('myForm').getCleanValues(), // Clear form values
                                );
                                $$('CountrySelect').setValue(
                                    $$('myForm').getCleanValues().CountrySelect, // Reset country selection
                                );
                                $$('Datepicker').setValue(''); // Clear datepicker value
                            },
                        },
                        {
                            view: 'button', // Button to consult the report
                            value: _('Consultar'), // Button label
                            hotkey: 'enter', // Hotkey for the button
                            type: 'form',
                            width: screen == 'small' ? 0 : 100, // Conditional width based on screen size
                            minWidth: 200,
                            click: () => {
                                const filterdata = this.getRoot().getValues(); // Get form values
                                if (devConfig.getCountryReport()) {
                                    filterdata.CountrySelect = profile.Settings.ReportCountry; // Set country selection based on profile
                                }
                                delete filterdata['CountrySelect2']; // Remove unnecessary field
                                if (profile.typeDate !== 2) {
                                    delete filterdata['Date']; // Remove date field if not applicable
                                } else {
                                    if (true) {
                                        delete filterdata['Datepicker']; // Remove datepicker if applicable
                                    }
                                }
                                this.app.callEvent('bonusReport:filter', [
                                    filterdata, // Call event with filter data
                                ]);
                                $$('b1').collapse(); // Collapse the filter section
                                $$('a5').scrollTo(100, 0); // Scroll to a specific position
                            },
                        },
                    ],
                },
            ],
        };

        // Accordion section 1 configuration
        const accordion_section1 = {
            type: 'wide',
            rows: [
                {
                    multi: true,
                    view: 'accordion', // Accordion component
                    css: 'betShop', // CSS class for styling
                    collapsed: false, // Initial state of the accordion
                    type: 'clean',
                    rows: [
                        {
                            header: _('Filtros'), // Header for filters
                            id: 'b1',
                            body: {
                                padding: 10,
                                rows: [
                                    upper_section, // Include upper section
                                    accordion_section, // Include accordion section
                                    buttons, // Include buttons section
                                ],
                            },
                        },
                    ],
                },
            ],
        };

        // Return the main form configuration
        return {
            view: 'form', // Form view
            id: 'myForm', // Form ID
            type: 'clean',
            rows: [accordion_section1], // Include accordion section 1
            rules: {
                fname: webix.rules.isNotEmpty, // Validation rule for non-empty fields
            },
        };
    }

    /**
     * Initializes the view and sets up event handlers.
     * @param {Object} grid - The grid object passed to the init method.
     */
    init(grid) {
        const lang = this.app.getService('locale').getLang(); // Get current language
        $$('Datepicker').attachEvent('onAfterRender', function () {
            devConfig.setDateModal(lang); // Set date modal based on language
        });
        webix.ready(function () {
            if (screen.width < 500) {
                $$('grav').hide(); // Hide element on small screens
            }
            if (profile.typeDate !== 2) {
                $$('Date').hide(); // Hide date input if not applicable
                $$('Datepicker').show(); // Show datepicker
            } else {
                if (true) {
                    $$('Date').show(); // Show date input
                    $$('Datepicker').hide(); // Hide datepicker
                }
            }
        });
    }
}

// FILE DOCUMENTED
